import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/daily-tracker`;
const usd = (n) => `$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd(n)}`;
const tone = (n) => (n > 0 ? 'text-emerald-300' : n < 0 ? 'text-red-400' : 'text-zinc-300');
const dayLabel = (k) => new Date(`${k}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const Stat = ({ label, value, cls = 'text-white', testid }) => (
  <div className="rounded-lg bg-zinc-950/60 border border-zinc-800 px-2 py-2 min-w-0">
    <p className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</p>
    <p className={`text-[13px] sm:text-lg font-bold font-mono whitespace-nowrap ${cls}`} data-testid={testid}>{value}</p>
  </div>
);

// Compact money summary for the top of the ticket tracker.
export const MoneyStrip = ({ refreshKey }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    const get = () => axios.get(API).then((r) => setD(r.data)).catch(() => {});
    get();
    const t = setInterval(get, 60000);
    window.addEventListener('ticket-money-changed', get);
    return () => { clearInterval(t); window.removeEventListener('ticket-money-changed', get); };
  }, [refreshKey]);
  if (!d) return null;
  const t = d.totals || {};
  const p = d.pending || {};
  const td = d.today || {};
  const pending = [
    ['Active', String(p.active ?? p.n ?? 0), 'text-sky-300', 'money-active'],
    ['Wagered', usd(p.stake), 'text-white', 'money-riding'],
    ['Could pay', usd(p.payout), 'text-emerald-300', 'money-could-pay'],
  ];
  const day = [
    ['Tickets', String(td.tickets || 0), 'text-sky-300', 'money-today-tickets'],
    ['Wagered', usd(td.stake), 'text-white', 'money-today-wagered'],
    ['Won back', usd(td.returned), 'text-emerald-300', 'money-today-returned'],
    ['Net', signed(td.net || 0), tone(td.net || 0), 'money-today'],
  ];
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-2.5 space-y-2" data-testid="money-strip">
      <Row title="Still pending" note="tickets in play right now" items={pending} cols="grid-cols-3" testid="money-pending-row" />
      <Row title="Full day" note={`${td.wins || 0}W · ${td.losses || 0}L · ${td.pending || 0} pending · placed today`} items={day} cols="grid-cols-4" testid="money-day-row" />
      <p className="text-[10px] text-zinc-500 text-center" data-testid="money-alltime">
        All-time <span className={`font-mono font-bold ${tone(t.net)}`}>{signed(t.net)}</span> · invested {usd(t.invested)}, won back {usd(t.returned)}
      </p>
    </div>
  );
};

const Row = ({ title, note, items, cols, testid }) => (
  <div className="rounded-lg border border-zinc-800/80 bg-zinc-950/40 px-2 py-1.5" data-testid={testid}>
    <p className="text-[10px] font-semibold text-zinc-300 mb-1">{title} <span className="font-normal text-zinc-500">· {note}</span></p>
    <div className={`grid ${cols} gap-x-1`}>
      {items.map(([k, v, cls, id]) => (
        <div key={k} className="min-w-0 text-center">
          <p className="text-[9px] uppercase tracking-wide text-zinc-500">{k}</p>
          <p className={`text-[12px] sm:text-base font-bold font-mono truncate ${cls}`} data-testid={id}>{v}</p>
        </div>
      ))}
    </div>
  </div>
);

// Money in vs money back, one row per day a ticket settled; open tickets shown as "still riding".
export default function DailyTracker() {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(API).then((r) => setD(r.data)).catch(() => setD({ days: [], totals: {}, pending: {} })); }, []);
  if (!d) return <div className="flex justify-center py-6 text-zinc-500" data-testid="daily-tracker-loading"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const t = d.totals || {};
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-4 space-y-3" data-testid="daily-tracker">
      <h2 className="text-base font-bold">Daily money tracker</h2>
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Invested" value={usd(t.invested)} testid="dt-total-invested" />
        <Stat label="Won back" value={usd(t.returned)} cls="text-emerald-300" testid="dt-total-returned" />
        <Stat label={t.net > 0 ? 'Up' : t.net < 0 ? 'Down' : 'Even'} value={signed(t.net)} cls={tone(t.net)} testid="dt-total-net" />
      </div>
      <p className="text-[11px] text-zinc-500">{t.wins || 0} won · {t.losses || 0} lost · counted on the day each ticket finished</p>
      {d.pending?.n > 0 && (
        <p className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-xs text-sky-200" data-testid="dt-pending">
          Still riding: <b>{usd(d.pending.stake)}</b> on {d.pending.n} ticket{d.pending.n === 1 ? '' : 's'} · could pay <b>{usd(d.pending.payout)}</b>
        </p>
      )}
      {!d.days.length ? <p className="text-sm text-zinc-500 py-3 text-center" data-testid="dt-empty">No finished tickets yet.</p> : (
        <table className="w-full text-[12px] sm:text-sm" data-testid="dt-table">
          <thead><tr className="text-[10px] uppercase text-zinc-500 text-right"><th className="text-left font-normal py-1">Day</th><th className="font-normal">In</th><th className="font-normal">Back</th><th className="font-normal">+/−</th></tr></thead>
          <tbody>
            {d.days.map((r) => (
              <tr key={r.date} className="border-t border-zinc-800 text-right font-mono" data-testid={`dt-row-${r.date}`}>
                <td className="text-left py-1.5 font-sans">
                  <span className="text-white text-[13px]">{dayLabel(r.date)}</span>
                  <span className="block text-[10px] text-zinc-500">{r.wins}W · {r.losses}L</span>
                </td>
                <td className="text-zinc-300">{usd(r.invested)}</td>
                <td className="text-emerald-300/90">{usd(r.returned)}</td>
                <td className={`font-bold ${tone(r.net)}`}>{signed(r.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
